import React from 'react';

import dayjs from '~/timeInit';

import { Box, Divider, Paper, Stack, Typography } from '@mui/material';

import LELogoIcon from '~/components/Icons/LELogo';

import type { CppInvoice } from '~/services/LuxLoyaltyService.types';

import InvoiceLineTable from './InvoiceLineTable';
import InvoiceRateGroupTable from './InvoiceRateGroupTable';
import { applyGst, currencyCode, formatCurrencyCents, formatGstRate, formatSignedPoints } from './invoiceFormat';
import { type InvoiceMonth, currentDay, formatMonthLabel } from './monthRange';
import { LUXURY_ESCAPES_ENTITY, type PartnerBilling } from './partnerBilling';

/**
 * Restores a row layout when printing.
 *
 * A printed page is roughly 816px wide, below MUI's `md` breakpoint (900px), so responsive columns
 * declared as `{ xs: 'column', md: 'row' }` collapse to a single stacked column on paper even
 * though they sit side by side on screen. Forcing the direction for print keeps the document
 * looking as designed without lowering the breakpoint and cramping narrow screens.
 */
const printRow = { '@media print': { flexDirection: 'row' } } as const;

/** As {@link printRow}, plus keeping the block whole — a payment advice split across two sheets is
 * unusable, since it is meant to be detached and returned in one piece. */
const printRowWhole = { '@media print': { flexDirection: 'row', breakInside: 'avoid' } } as const;

/**
 * How the invoice presents its line items.
 *
 * `daily` lists every day of the period; `rate-group` aggregates the same money into one row per
 * CPP tier that applied. Both price identically — this only changes how the charge is presented.
 */
export type InvoiceFormat = 'daily' | 'rate-group';

/** DOM id the print stylesheet isolates. */
export const INVOICE_PRINT_ID = 'cpp-invoice-print';

interface Props {
  invoice: CppInvoice;
  billing: PartnerBilling;
  month: InvoiceMonth;
  /** The number Finance is raising this invoice under; empty until they enter one. */
  invoiceNumber: string;
  /** The date the invoice was generated, as `YYYY-MM-DD`; empty before then. */
  invoiceDate: string;
  /** The due date Finance chose when generating, as `YYYY-MM-DD`; empty before then. */
  dueDate: string;
  format: InvoiceFormat;
  /** Daily format only: drop days that billed nothing and moved no points. */
  hideEmptyDays?: boolean;
}

/** A small label above a value, as the issued invoice sets its metadata. */
function LabelledValue({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box mb={0.75}>
      <Typography variant="caption" fontWeight={700} display="block">
        {label}
      </Typography>
      <Typography variant="body2">{children}</Typography>
    </Box>
  );
}

/** One right-aligned figure in the totals block. */
function TotalRow({ label, value, emphasis = false }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={4} py={0.5}>
      <Typography variant="body2" fontWeight={emphasis ? 700 : 400}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={emphasis ? 700 : 400}>
        {value}
      </Typography>
    </Stack>
  );
}

/** The Luxury Escapes address block, used in the header and again on the payment advice. */
function IssuerAddress() {
  return (
    <>
      <Typography variant="body2">{LUXURY_ESCAPES_ENTITY.name}</Typography>
      {LUXURY_ESCAPES_ENTITY.addressLines.map((line) => (
        <Typography key={line} variant="body2">
          {line}
        </Typography>
      ))}
    </>
  );
}

/**
 * The partner tax invoice, following the layout Luxury Escapes already issues (INV-2482).
 *
 * Luxury Escapes bills the partner, so the document carries *our* payment details: the partner's
 * name and address identify who is being billed, but the account the money moves into is ours.
 * The partner's own bank details never appear.
 *
 * The billed period's daily activity is the line-item table in the middle. Figures come from the
 * service; the issuing-entity block is a constant and the partner's name, ABN and address come
 * from the partner record, rendering as `—` while it carries none rather than as a plausible
 * default.
 */
export default function PartnerInvoiceDocument({
  invoice,
  billing,
  month,
  invoiceNumber,
  invoiceDate,
  dueDate,
  format,
  hideEmptyDays = false,
}: Props) {
  const { totals, currency } = invoice;
  const { gstCents, totalCents } = applyGst(totals.amountCents, billing.gstRate);

  // Both are stamped when the invoice is generated: the issue date is the day it was raised, not
  // the day the period closed, and the due date is agreed commercially rather than derived from
  // payment terms. Each falls back so the on-screen preview is never blank before generating.
  const issuedOn = invoiceDate ? dayjs(invoiceDate) : dayjs(currentDay());
  const due = dueDate ? dayjs(dueDate) : issuedOn.add(LUXURY_ESCAPES_ENTITY.paymentTermsDays, 'day');
  const totalPayable = formatCurrencyCents(totalCents, currency);

  return (
    <Paper id={INVOICE_PRINT_ID} variant="outlined" sx={{ p: 4 }}>
      <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} mb={2}>
        <LELogoIcon sx={{ fontSize: 36 }} />
        <Typography variant="h5" letterSpacing={0.5}>
          <Box component="span" fontWeight={300}>
            LUXURY
          </Box>
          <Box component="span" fontWeight={700}>
            ESCAPES
          </Box>
        </Typography>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="space-between" sx={printRow}>
        <Box flex={1}>
          <Typography variant="h4">TAX INVOICE</Typography>
          <Box mt={1} ml={{ md: 3 }} sx={{ '@media print': { ml: 3 } }}>
            <Typography variant="body2" color={billing.legalName ? 'text.primary' : 'text.disabled'}>
              {billing.legalName || '—'}
            </Typography>
            <Typography variant="body2" color={billing.address ? 'text.primary' : 'text.disabled'}>
              {billing.address ?? '—'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ minWidth: 180 }}>
          <LabelledValue label="Invoice Date">{issuedOn.format('D MMM YYYY')}</LabelledValue>
          <LabelledValue label="Invoice Number">
            {invoiceNumber || (
              <Box component="span" color="text.disabled">
                —
              </Box>
            )}
          </LabelledValue>
          <LabelledValue label="ABN">{LUXURY_ESCAPES_ENTITY.abn}</LabelledValue>
        </Box>

        <Box sx={{ minWidth: 200 }}>
          <IssuerAddress />
        </Box>
      </Stack>

      <Box mt={3}>
        <Typography variant="body2" gutterBottom>
          Société points issued for {formatMonthLabel(month)}
        </Typography>
        {format === 'daily' ? (
          <InvoiceLineTable invoice={invoice} hideEmptyDays={hideEmptyDays} />
        ) : (
          <InvoiceRateGroupTable invoice={invoice} />
        )}
      </Box>

      <Stack alignItems="flex-end" mt={3}>
        <Box sx={{ minWidth: 340 }}>
          <TotalRow label="Total points" value={formatSignedPoints(totals.netPoints)} />
          <Divider sx={{ my: 1 }} />
          <TotalRow label="Subtotal" value={formatCurrencyCents(totals.amountCents, currency)} />
          <TotalRow label={`GST (${formatGstRate(billing.gstRate)})`} value={formatCurrencyCents(gstCents, currency)} />
          <Divider sx={{ my: 1 }} />
          <TotalRow label={`TOTAL ${currencyCode(currency)}`} value={totalPayable} emphasis />
        </Box>
      </Stack>

      <Box mt={5}>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          Due Date: {due.format('D MMM YYYY')}
        </Typography>
        <Typography variant="body2">Account Name: {LUXURY_ESCAPES_ENTITY.bank.accountName}</Typography>
        <Typography variant="body2">Bank: {LUXURY_ESCAPES_ENTITY.bank.bank}</Typography>
        <Typography variant="body2">BSB: {LUXURY_ESCAPES_ENTITY.bank.bsb}</Typography>
        <Typography variant="body2">Account Number: {LUXURY_ESCAPES_ENTITY.bank.accountNumber}</Typography>
        <Typography variant="body2">Swift Code: {LUXURY_ESCAPES_ENTITY.bank.swiftCode}</Typography>
        <Typography variant="body2">Bank Currency: {LUXURY_ESCAPES_ENTITY.bank.currency}</Typography>
        <Typography variant="body2" mt={2}>
          Please send remittance advice to: {LUXURY_ESCAPES_ENTITY.remittanceEmail}
        </Typography>
      </Box>

      <Divider sx={{ my: 5, borderStyle: 'dashed' }} />

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} justifyContent="space-between" sx={printRowWhole}>
        <Box flex={1}>
          <Typography variant="h4" gutterBottom>
            PAYMENT ADVICE
          </Typography>
          <Stack direction="row" spacing={2} mt={3} ml={{ md: 3 }}>
            <Typography variant="body2" fontWeight={700}>
              To:
            </Typography>
            <Box>
              <IssuerAddress />
            </Box>
          </Stack>
        </Box>

        <Box sx={{ minWidth: 320 }}>
          <Stack direction="row" justifyContent="space-between" spacing={2} py={0.5}>
            <Typography variant="body2" fontWeight={700}>
              Customer
            </Typography>
            <Typography variant="body2">{billing.legalName || '—'}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={2} py={0.5}>
            <Typography variant="body2" fontWeight={700}>
              Invoice Number
            </Typography>
            <Typography variant="body2" color={invoiceNumber ? 'text.primary' : 'text.disabled'}>
              {invoiceNumber || '—'}
            </Typography>
          </Stack>
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" justifyContent="space-between" spacing={2} py={0.5}>
            <Typography variant="body2" fontWeight={700}>
              Amount Due
            </Typography>
            <Typography variant="body2">{totalPayable}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between" spacing={2} py={0.5}>
            <Typography variant="body2" fontWeight={700}>
              Due Date
            </Typography>
            <Typography variant="body2">{due.format('D MMM YYYY')}</Typography>
          </Stack>
          <Divider sx={{ my: 1 }} />
          <Typography variant="body2" fontWeight={700}>
            Amount Enclosed
          </Typography>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', height: 28 }} />
          <Typography variant="caption" color="text.secondary">
            Enter the amount you are paying above
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}
